import React from 'react';
import { useTranslation } from 'react-i18next';

import { AddIcon } from '@/shared/assets/icons/add-icon';
import { DrawerController } from '@/shared/components/ui/drawer-controller/drawer-controller';
import { Form } from '@/shared/components/ui/form/form';
import { TSubmittedData } from '@/shared/components/ui/form/form.types';
import { Trigger } from '@/shared/components/ui/trigger/trigger';
import { AddResourcesToGroupForm } from '@/shared/features/groups/components/add-resources-to-group/add-resources-to-group-form';
import { TAddResourcesToGroupProps } from '@/shared/features/groups/components/add-resources-to-group/add-resources-to-group.types';
import {
  addResourcesToGroupInputSchema,
  TAddResourcesToGroupInputSchema,
} from '@/shared/features/groups/components/add-resources-to-group/add-resources-to-group.validation';
import { useGroupsConfig } from '@/shared/features/groups/context/groups-config-context';
import { useGroupResourceActions } from '@/shared/features/groups/hooks/use-group-resource-actions/use-group-resource-actions';
import { Can } from '@/shared/features/permissions/components/can/can';

export const AddResourcesToGroup: React.FC<TAddResourcesToGroupProps> = ({
  resources,
  onClose,
  onSuccess,
  component,
  permission,
}) => {
  const { t } = useTranslation();
  const { permissionActions } = useGroupsConfig();
  const { handleAddResourcesToGroup } = useGroupResourceActions();

  const onSubmit = async (
    data: TSubmittedData<TAddResourcesToGroupInputSchema>,
  ) => {
    await handleAddResourcesToGroup(data, resources, onSuccess, onClose);
  };

  return (
    <Can action={permissionActions.updateGroup}>
      <DrawerController
        trigger={
          <Trigger
            icon={<AddIcon />}
            component={component}
            label={t('groups:add_to_group')}
            buttonVariant="secondary"
          />
        }
        title={t('groups:add_to_group')}
        permission={permission}
      >
        {(handleClose) => (
          <Form<TAddResourcesToGroupInputSchema>
            onSubmit={onSubmit}
            schema={addResourcesToGroupInputSchema}
          >
            {() => (
              <AddResourcesToGroupForm
                resources={resources}
                handleClose={handleClose}
              />
            )}
          </Form>
        )}
      </DrawerController>
    </Can>
  );
};
